import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import { PUBLIC_SECTION_SURFACES } from './home/publicSectionSurfaces';
import { PublicSectionWaveTop } from './home/PublicSectionWave';
import PublicReveal, { publicRevealStagger } from './PublicReveal';
import type { PublicFiliere, PublicFiliereTrack } from '../../utils/publicFilieres';
import { buildPublicFiliereDetailPath } from '../../utils/publicFilieres';
import type { PublicTarifCategoryDisplay } from '../../utils/academiaTarifs';
import {
  filterCohortesByFiliereId,
  filterPlanningByCohorteId,
  pickReferenceCohorte,
  type PublicCohorteRow,
  type PublicPlanningSession,
} from '../../utils/publicPlanningDisplay';
import { isCohorteFull } from '../../utils/cohorteCapacityMessage';
import { resolveFiliereCoverImage } from '../../utils/trackCoverImage';
import '../../styles/publicSectionWave.css';
import '../../styles/publicFiliereTrackProgrammes.css';

type Props = {
  track: PublicFiliereTrack;
  cohortes: Array<PublicCohorteRow & { idFiliere?: string }>;
  planningByFiliereId: Record<string, PublicPlanningSession[]>;
  pricingCategories: PublicTarifCategoryDisplay[];
  pricingCategoriesForFiliere?: (idFiliere: string) => PublicTarifCategoryDisplay[];
};

const ALL_FILTER = '__all__';

function resolveFiliereCover(filiere: PublicFiliere, track: PublicFiliereTrack): string {
  return resolveFiliereCoverImage(filiere, {
    domaineUrlImage: track.urlImage,
    trackSlug: track.slug,
  });
}

function lowestInscriptionPrice(categories: PublicTarifCategoryDisplay[]): string {
  const amounts = categories
    .map((row) => row.inscription)
    .filter((value) => value && value !== '—');
  const amount = amounts[0];
  return amount ? `Inscription ${amount}` : 'Sur devis';
}

function countStudents(cohortes: PublicCohorteRow[]): number {
  return cohortes.reduce((sum, c) => sum + (c.capacity?.inscrits ?? 0), 0);
}

function placesDisponibles(cohortes: PublicCohorteRow[]): number {
  return cohortes
    .filter((c) => !isCohorteFull(c))
    .reduce((sum, c) => sum + (c.capacity?.placesRestantes ?? 0), 0);
}

function cohortesMetaLabel(cohortes: PublicCohorteRow[]): string {
  if (cohortes.length === 0) return '0 cohorte';
  const places = placesDisponibles(cohortes);
  const allFull = cohortes.every((c) => isCohorteFull(c));
  if (allFull) return 'Plus de places disponibles';
  if (places > 0) {
    return `${places} place${places > 1 ? 's' : ''} · ${cohortes.length} cohorte${cohortes.length > 1 ? 's' : ''}`;
  }
  return `${cohortes.length} cohorte${cohortes.length > 1 ? 's' : ''}`;
}

function durationBadge(cohortes: PublicCohorteRow[]): string {
  const withDates = cohortes.find((c) => c.dateDebut && c.dateFin);
  if (withDates?.dateDebut && withDates.dateFin) {
    const start = new Date(withDates.dateDebut);
    const end = new Date(withDates.dateFin);
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      const days = Math.max(0, (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      const months = Math.max(1, Math.round(days / 30));
      return `${String(months).padStart(2, '0')} MOIS`;
    }
  }
  return '02 MOIS';
}

type ProgrammeCardProps = {
  filiere: PublicFiliere;
  track: PublicFiliereTrack;
  cohortes: PublicCohorteRow[];
  planningSessions: PublicPlanningSession[];
  priceLabel: string;
};

function ProgrammeGridCard({
  filiere,
  track,
  cohortes,
  planningSessions,
  priceLabel,
}: ProgrammeCardProps) {
  const cover = resolveFiliereCover(filiere, track);
  const students = countStudents(cohortes);
  const reference = pickReferenceCohorte(cohortes);
  const referenceSessions = reference
    ? filterPlanningByCohorteId(planningSessions, reference.id)
    : [];
  const seances =
    referenceSessions.length > 0
      ? referenceSessions.length
      : cohortes.length <= 1 && planningSessions.length > 0
        ? planningSessions.length
        : planningSessions.length === 0
          ? 3
          : null;
  const seancesLabel =
    seances == null ? 'Planning' : `${seances} séance${seances > 1 ? 's' : ''}/sem.`;
  const detailPath = buildPublicFiliereDetailPath(track.slug, filiere);

  return (
    <article className="public-track-programme-card">
      <div className="public-track-programme-card__media">
        <img src={cover} alt="" loading="lazy" decoding="async" />
        <span className="public-track-programme-card__badge">{durationBadge(cohortes)}</span>
      </div>

      <div className="public-track-programme-card__body">
        <div className="public-track-programme-card__rating" aria-hidden>
          {Array.from({ length: 5 }).map((_, i) => (
            <LucideSymbolIcon key={i} name="star" tone="inherit" size={14} className="text-amber-400" />
          ))}
          <span>(Certifiante)</span>
        </div>

        <h3 className="public-track-programme-card__title">{filiere.libelle}</h3>

        <div className="public-track-programme-card__meta">
          <span className="public-track-programme-card__meta-item">
            <LucideSymbolIcon name="menu_book" tone="muted" size={15} />
            {seancesLabel}
          </span>
          <span className="public-track-programme-card__meta-item">
            <LucideSymbolIcon name="groups" tone="muted" size={15} />
            {students > 0
              ? `${students} inscrit${students > 1 ? 's' : ''} · ${cohortesMetaLabel(cohortes)}`
              : cohortesMetaLabel(cohortes)}
          </span>
          <span className="public-track-programme-card__meta-item public-track-programme-card__meta-item--price">
            <LucideSymbolIcon name="payments" tone="muted" size={15} />
            {priceLabel}
          </span>
        </div>
      </div>

      <div className="public-track-programme-card__footer">
        <Link to={detailPath} className="public-track-programme-card__cta">
          En savoir plus
        </Link>
      </div>
    </article>
  );
}

export default function PublicFiliereTrackProgrammes({
  track,
  cohortes,
  planningByFiliereId,
  pricingCategories,
  pricingCategoriesForFiliere,
}: Props) {
  const [activeFilter, setActiveFilter] = useState<string>(ALL_FILTER);

  const visibleFilieres = useMemo(() => {
    if (activeFilter === ALL_FILTER) return track.filieres;
    return track.filieres.filter((f) => f.id === activeFilter);
  }, [activeFilter, track.filieres]);

  return (
    <section id="programmes" className="public-track-programmes-wave-section scroll-mt-24">
      <PublicSectionWaveTop fill={PUBLIC_SECTION_SURFACES.aboutEnd} animated />
      <div className="public-section-wave__inner">
        <div className="relative z-[1] mx-auto max-w-7xl px-4 sm:px-6">
          <PublicReveal animation="fade-in-up" className="mb-10 text-center">
            <p className="public-track-programmes__intro-eyebrow text-xs font-bold uppercase tracking-widest">
              Programmes
            </p>
            <h2 className="public-track-programmes__intro-title mx-auto mt-4 max-w-3xl text-3xl font-bold sm:text-4xl">
              Formations de ce domaine
            </h2>
            <p className="public-track-programmes__intro-subtitle mx-auto mt-4 max-w-2xl text-base">
              Choisissez le parcours qui correspond à votre ambition, puis inscrivez-vous en ligne pour rejoindre la
              prochaine cohorte à Kinshasa.
            </p>
          </PublicReveal>

          <PublicReveal animation="fade-in-up" delay={80} className="mb-10">
            <div className="public-track-programmes__filters" role="tablist" aria-label="Filtrer les formations">
              <button
                type="button"
                role="tab"
                aria-selected={activeFilter === ALL_FILTER}
                className={`public-track-programmes__filter group ${activeFilter === ALL_FILTER ? 'is-active' : ''}`}
                onClick={() => setActiveFilter(ALL_FILTER)}
              >
                <span className="public-track-programmes__filter-icon">
                  <LucideSymbolIcon
                    name={track.icon}
                    tone="inherit"
                    size={22}
                    className={activeFilter === ALL_FILTER ? 'text-white' : 'text-primary group-hover:text-white'}
                  />
                </span>
                <span>
                  <span className="public-track-programmes__filter-label">Toutes</span>
                  <span className="public-track-programmes__filter-count">
                    {track.filieres.length} formation{track.filieres.length > 1 ? 's' : ''}
                  </span>
                </span>
              </button>

              {track.filieres.map((filiere) => {
                const filiereCohortes = filterCohortesByFiliereId(filiere.id, cohortes);
                const isActive = activeFilter === filiere.id;
                return (
                  <button
                    key={filiere.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`public-track-programmes__filter group ${isActive ? 'is-active' : ''}`}
                    onClick={() => setActiveFilter(filiere.id)}
                  >
                    <span className="public-track-programmes__filter-icon">
                      <LucideSymbolIcon
                        name={track.icon}
                        tone="inherit"
                        size={22}
                        className={isActive ? 'text-white' : 'text-primary group-hover:text-white'}
                      />
                    </span>
                    <span className="min-w-0">
                      <span className="public-track-programmes__filter-label line-clamp-1">{filiere.libelle}</span>
                      <span className="public-track-programmes__filter-count">
                        {filiereCohortes.length} cohorte{filiereCohortes.length > 1 ? 's' : ''}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </PublicReveal>

          <div className="public-track-programmes__grid">
            {visibleFilieres.map((filiere, index) => {
              const filiereCohortes = filterCohortesByFiliereId(filiere.id, cohortes);
              const categories =
                pricingCategoriesForFiliere?.(filiere.id) ?? pricingCategories;
              const priceLabel = lowestInscriptionPrice(categories);
              return (
                <PublicReveal key={filiere.id} animation="fade-in-up" delay={publicRevealStagger(index, 80, 120)}>
                  <ProgrammeGridCard
                    filiere={filiere}
                    track={track}
                    cohortes={filiereCohortes}
                    planningSessions={planningByFiliereId[filiere.id] ?? []}
                    priceLabel={priceLabel}
                  />
                </PublicReveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
